import { AssistantCommitAction, AssistantNavigation, EnrichmentPatchEvent } from '../types';
import { parseNavigateEvent } from './navigateEvent';

export const isEnrichmentPatchEvent = (value: unknown): value is EnrichmentPatchEvent => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const patch = value as Record<string, unknown>;
  return (
    typeof patch.field === 'string' && typeof patch.suggestion_id === 'string' && 'value' in patch
  );
};

export const isCommitActionEvent = (value: unknown): value is AssistantCommitAction => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const action = value as Record<string, unknown>;
  return (
    typeof action.action_id === 'string' &&
    typeof action.status === 'string' &&
    typeof action.tool === 'string' &&
    typeof action.summary === 'string'
  );
};

export const parseCommitActionEvent = (rawData: string): AssistantCommitAction | null => {
  try {
    const parsed = JSON.parse(rawData) as unknown;
    if (isCommitActionEvent(parsed)) {
      return parsed;
    }

    if (typeof parsed === 'string') {
      const nestedParsed = JSON.parse(parsed) as unknown;
      if (isCommitActionEvent(nestedParsed)) {
        return nestedParsed;
      }
    }
  } catch {
    return null;
  }

  return null;
};

export const extractStatusText = (rawData: string): string | null => {
  if (!rawData) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawData);
    if (typeof parsed === 'string') {
      return parsed;
    }
    if (parsed && typeof parsed === 'object') {
      const maybeObject = parsed as Record<string, unknown>;
      const potentialText =
        maybeObject['status_text'] ?? maybeObject['statusText'] ?? maybeObject['status'];
      if (typeof potentialText === 'string' && potentialText.trim()) {
        return potentialText;
      }
    }
  } catch {
    if (rawData.trim()) {
      return rawData;
    }
  }

  return null;
};

export interface ChatStreamEventCallbacks {
  onMessage?: (text: string, textId?: number) => void;
  onWholeMessage?: (message: string, textId: number) => void;
  onSequenceStatus?: (status: string, textId: number) => void;
  onStatusText?: (statusText: string | null) => void;
  onQuestion?: (question: any) => void;
  onCommitAction?: (action: AssistantCommitAction) => void;
  onNavigate?: (navigation: AssistantNavigation) => void;
  onResultOut?: (result: any) => void;
  onEnrichmentPatch?: (patch: EnrichmentPatchEvent) => void;
}

/**
 * Shared per-event-type parser for the chat stream, used both by the live
 * stream (useSSEStreaming.ts) and by the job-recovery poll replay
 * (useChatJobRecovery.ts) — so a resumed job renders through the exact same
 * logic a live stream would have used. `job`, `stream_end`/`close` and
 * `error` are lifecycle events handled by the caller, not here.
 */
export const applyChatSSEEvent = (
  eventType: string,
  rawData: string,
  callbacks: ChatStreamEventCallbacks,
  isV4Enabled: boolean
): void => {
  switch (eventType) {
    case 'summary_delta': {
      try {
        const data = JSON.parse(rawData);
        callbacks.onMessage?.(data?.delta || '', data?.text_id ?? 0);
      } catch (parseError) {
        console.error('Failed to parse summary_delta event:', parseError);
      }
      break;
    }
    case 'status_text':
    case 'status_update':
    case 'status': {
      const text = extractStatusText(rawData);
      if (text !== null) {
        callbacks.onStatusText?.(text);
      }
      break;
    }
    case 'sequence': {
      // v4 only: one event per bubble. `message` is the complete final text;
      // `status` can arrive first for that text_id (quick-ack / tool status).
      if (!isV4Enabled) {
        break;
      }
      try {
        const parsed = JSON.parse(rawData);
        const textId: number = parsed?.text_id ?? 0;
        if (typeof parsed?.status === 'string' && parsed.status) {
          callbacks.onSequenceStatus?.(parsed.status, textId);
        }
        if (typeof parsed?.message === 'string' && parsed.message) {
          callbacks.onWholeMessage?.(parsed.message, textId);
        }
      } catch (parseError) {
        console.error('Failed to parse sequence event:', parseError);
      }
      break;
    }
    case 'question': {
      try {
        const data = JSON.parse(rawData);
        callbacks.onQuestion?.(data);
      } catch (parseError) {
        console.error('Failed to parse question event:', parseError);
      }
      break;
    }
    case 'commit_action': {
      try {
        const data = parseCommitActionEvent(rawData);
        if (data) {
          callbacks.onCommitAction?.(data);
        }
      } catch (parseError) {
        console.error('Failed to parse commit_action event:', parseError);
      }
      break;
    }
    case 'navigate': {
      const data = parseNavigateEvent(rawData);
      if (data) {
        callbacks.onNavigate?.(data);
      }
      break;
    }
    case 'result_json': {
      try {
        callbacks.onResultOut?.(rawData);
      } catch (parseError) {
        console.error('Failed to parse result_json event:', parseError);
      }
      break;
    }
    case 'enrichment_patch': {
      try {
        const patchData = JSON.parse(rawData) as unknown;
        if (isEnrichmentPatchEvent(patchData)) {
          callbacks.onEnrichmentPatch?.(patchData);
        }
      } catch (parseError) {
        console.error('Failed to parse enrichment_patch event:', parseError);
      }
      break;
    }
    default:
      break;
  }
};
